import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import OpenIcon from '@mulesoft/anypoint-icons/lib/svg/open.svg';
import { AssetType, Rating, Table } from '@mulesoft/exchange-ui-components';
import { AssetPropType } from '@mulesoft/exchange-react-shapes';
import withLayoutContext from '~/components/LayoutContextProvider';
import AssetCreatedBy from '~/components/Asset/CreatedBy';
import FormattedDate from '~/components/Layout/FormattedDate';
import InfiniteScroll from '~/components/Layout/InfiniteScroll';
import { REMOVE_MULESOFT_ORG_REVIEWS_ENABLED } from '~/domains/featureFlags/keys';
import styles from './AssetList.css';

const getHeaders = (showRating, isPublicPortal) => {
  const headers = [
    {
      key: 'name',
      label: 'Name'
    },
    {
      key: 'type',
      label: 'Type'
    },
    {
      key: 'modifiedAt',
      label: 'Date Modified'
    }
  ];

  if (showRating) {
    headers.push({
      key: 'rating',
      label: 'Rating'
    });
  }

  if (!isPublicPortal) {
    headers.push({
      key: 'createdBy',
      label: 'Created By'
    });
  }

  return headers;
};

class AssetList extends React.Component {
  static propTypes = {
    id: PropTypes.string,
    assets: PropTypes.arrayOf(AssetPropType),
    query: PropTypes.object,
    onAssetClick: PropTypes.func,
    onLoadAssets: PropTypes.func.isRequired,
    featureFlags: PropTypes.object,
    hasMoreAssets: PropTypes.bool,
    onReachEnd: PropTypes.func,
    isPublicPortal: PropTypes.bool,
    getPath: PropTypes.func
  };

  loadMoreAssets = () => {
    const query = {
      offset: this.props.assets.length,
      ...this.props.query
    };

    this.props.onReachEnd();

    return this.props.onLoadAssets(query);
  };

  render() {
    const {
      id = '',
      assets,
      hasMoreAssets,
      isPublicPortal,
      featureFlags,
      getPath,
      onAssetClick
    } = this.props;
    const showRating = !featureFlags?.[REMOVE_MULESOFT_ORG_REVIEWS_ENABLED];

    return (
      <InfiniteScroll
        hasMore={hasMoreAssets}
        onLoadMore={this.loadMoreAssets}
        loadingMoreMessage="Loading more assets ..."
        loadMoreMessage="Load more assets"
        testId="assets"
      >
        <div id={id} className={styles.assetList} data-test-id="asset-list">
          <Table
            caption="Asset results"
            testId="assets"
            className={styles.table}
            headers={getHeaders(showRating, isPublicPortal)}
            rows={mapAssets(
              assets,
              isPublicPortal,
              getPath,
              onAssetClick,
              showRating
            )}
            emptyMessage="We couldn't find results that match your criteria."
          />
        </div>
      </InfiniteScroll>
    );
  }
}

function mapAssets(assets, isPublicPortal, getPath, onClick, showRating) {
  return assets.map((asset) => {
    const assetRow = {
      name: <AssetName asset={asset} getPath={getPath} onClick={onClick} />,
      type: <AssetType type={asset.type} size="xs" />,
      modifiedAt: (
        <FormattedDate testId="asset-modified-date" date={asset.modifiedAt} />
      ),
      key: asset.id
    };

    if (showRating) {
      assetRow.rating = (
        <Rating
          testId="asset-rating"
          rating={asset.rating}
          className={styles.rating}
          disabled
        />
      );
    }

    if (!isPublicPortal) {
      assetRow.createdBy = <AssetCreatedBy user={asset.createdBy} />;
    }

    return assetRow;
  });
}

class AssetName extends React.Component {
  static propTypes = {
    asset: AssetPropType,
    getPath: PropTypes.func,
    onClick: PropTypes.func
  };

  static defaultProps = {
    onClick: () => {}
  };

  onClick = () => {
    const { onClick, asset } = this.props;

    onClick(asset);
  };

  render() {
    const { asset, getPath } = this.props;

    return (
      <div className={styles.name}>
        <Link to={getPath('assetHome', asset)} onClick={this.onClick}>
          <span className="visually-hidden">Go to asset</span>
          {asset.name}
        </Link>
        <Icon className={styles.openIcon} size="xxs">
          <OpenIcon />
        </Icon>
      </div>
    );
  }
}

export { AssetList, AssetName };

export default withLayoutContext()(AssetList);
